import { isAxiosError } from 'axios'
import {
  REWRITE_GATE_UNAVAILABLE_ERROR_CODE,
  REWRITE_INSUFFICIENT_SOURCES_ERROR_CODE,
  REWRITE_INSTRUCTION_REFUSED_ERROR_CODE,
  REWRITE_INVALID_OUTPUT_ERROR_CODE,
} from '@artem-lex/compliance-documents'

export type RewriteErrorAction =
  | { type: 'insufficient-sources-dialog' }
  | { type: 'instruction-refused-dialog' }
  | { type: 'retry-toast' }
  | { type: 'rethrow' }

export type RewriteErrorDetails = {
  code: string | null
  blockReason?: string
  availableSourceNames?: string[]
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function readAvailableSourceNames(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  const names = value.filter((entry): entry is string => typeof entry === 'string')
  return names.length > 0 ? names : undefined
}

function readRecordCode(record: Record<string, unknown>): string | null {
  const code = record.code
  return typeof code === 'string' ? code : null
}

export function readRewriteErrorDetails(error: unknown): RewriteErrorDetails {
  if (isAxiosError(error) && error.response?.status === 400) {
    const data = error.response.data
    if (typeof data === 'object' && data != null) {
      const record = data as Record<string, unknown>
      return {
        code: readRecordCode(record),
        ...(readOptionalString(record.blockReason) != null
          ? { blockReason: readOptionalString(record.blockReason) }
          : {}),
        ...(readAvailableSourceNames(record.availableSourceNames) != null
          ? {
              availableSourceNames: readAvailableSourceNames(
                record.availableSourceNames
              ),
            }
          : {}),
      }
    }
  }

  if (typeof error === 'object' && error != null && 'code' in error) {
    const record = error as Record<string, unknown>
    return {
      code: readRecordCode(record),
      ...(readOptionalString(record.blockReason) != null
        ? { blockReason: readOptionalString(record.blockReason) }
        : {}),
      ...(readAvailableSourceNames(record.availableSourceNames) != null
        ? {
            availableSourceNames: readAvailableSourceNames(
              record.availableSourceNames
            ),
          }
        : {}),
    }
  }

  return { code: null }
}

function readRewriteErrorCode(error: unknown): string | null {
  return readRewriteErrorDetails(error).code
}

export function resolveRewriteApiErrorAction(error: unknown): RewriteErrorAction {
  const code = readRewriteErrorCode(error)
  if (code == null) {
    return { type: 'rethrow' }
  }
  if (code === REWRITE_INSUFFICIENT_SOURCES_ERROR_CODE) {
    return { type: 'insufficient-sources-dialog' }
  }
  if (code === REWRITE_INSTRUCTION_REFUSED_ERROR_CODE) {
    return { type: 'instruction-refused-dialog' }
  }
  if (
    code === REWRITE_INVALID_OUTPUT_ERROR_CODE ||
    code === REWRITE_GATE_UNAVAILABLE_ERROR_CODE
  ) {
    return { type: 'retry-toast' }
  }
  return { type: 'rethrow' }
}
